import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  ArrowRightLeft,
  Plus,
  Search,
  RefreshCcw,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

// ---- helpers ----
function isValidDDMMYYYY(s) {
  if (!s) return true;
  return /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.test(String(s).trim());
}

function parseDDMMYYYY(s) {
  if (!s) return null;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(s).trim());
  if (!m) return null;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  const d = new Date(yyyy, mm - 1, dd);
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd)
    return null;
  return d;
}

function validateFromTo(fromStr, toStr) {
  if (fromStr && !isValidDDMMYYYY(fromStr))
    return "Invalid From Date (use dd/MM/yyyy)";
  if (toStr && !isValidDDMMYYYY(toStr))
    return "Invalid To Date (use dd/MM/yyyy)";

  const from = parseDDMMYYYY(fromStr);
  const to = parseDDMMYYYY(toStr);
  const today = new Date();
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  if (from) {
    const f = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    if (f > todayOnly) return "Select from date lesser than current date";
  }
  if (to) {
    const t = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    if (t > todayOnly)
      return "Select to date lesser than or Equal to Current date";
  }
  if (from && to) {
    const f = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const t = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    if (t < f) return "Please select from date less than to date";
  }
  return null;
}

// ---- date <input> conversion helpers ----
function isoToDDMMYYYY(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return "";
  return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
}

function ddmmyyyyToISO(s) {
  if (!s) return "";
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(s).trim());
  if (!m) return "";
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
}

const EmployeeTransferListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || ""; // login branch
  const branchType = localStorage.getItem("branchType") || "HO"; // HO / others

  const screenBadge = useMemo(() => "INFRA SCREEN : C-18", []);

  const [showFilters, setShowFilters] = useState(true);

  // meta
  const [fromSites, setFromSites] = useState([]);
  const [toSites, setToSites] = useState([]);
  const [fromSite, setFromSite] = useState("Select");
  const [toSite, setToSite] = useState("Select");

  // search fields
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [fromDate, setFromDate] = useState(""); // dd/MM/yyyy
  const [toDate, setToDate] = useState(""); // dd/MM/yyyy

  // autocomplete
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);
  const [codeOpen, setCodeOpen] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination (server)
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // selection
  const [approveEmpIds, setApproveEmpIds] = useState([]); // empid
  const [deleteTransferIds, setDeleteTransferIds] = useState([]); // transferId
  const [approveSelectAll, setApproveSelectAll] = useState(false);
  const [deleteSelectAll, setDeleteSelectAll] = useState(false);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const approveBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (approveEmpIds.length === 0
      ? "bg-gray-400 cursor-not-allowed"
      : "bg-emerald-600 hover:bg-emerald-700");

  const deleteBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (deleteTransferIds.length === 0
      ? "bg-gray-400 cursor-not-allowed"
      : "bg-red-600 hover:bg-red-700");

  const axiosHeaders = () => ({
    "x-loginuserid":
      localStorage.getItem("loginuserid") ||
      localStorage.getItem("loginUserId") ||
      "",
  });

  const loadMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(branchId).trim()) {
      toast.warn("branchId missing in localStorage.");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.get(
        `${BASE_URL}/api/payroll/employee-transfer/meta`,
        {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(branchId),
            branchType: String(branchType),
          },
          headers: axiosHeaders(),
          timeout: 60000,
        },
      );

      if (!data?.success) {
        toast.error(data?.message || "Meta load failed");
        return;
      }

      setFromSites(data.fromSites || []);
      setToSites(data.toSites || []);
      const def = data.defaultFromSiteId
        ? String(data.defaultFromSiteId)
        : String(branchId);

      setFromSite(def || "Select");
      setToSite("Select");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(branchId).trim()) {
      toast.warn("branchId missing in localStorage.");
      return;
    }

    const dateErr = validateFromTo(fromDate, toDate);
    if (dateErr) {
      toast.warn(dateErr);
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.get(
        `${BASE_URL}/api/payroll/employee-transfer/list`,
        {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(branchId),

            fromSite,
            toSite,
            fromDate: fromDate.trim(),
            toDate: toDate.trim(),
            empCode: empCode.trim(),
            empName: empName.trim(),

            page: p,
            pageSize,
          },
          headers: axiosHeaders(),
          timeout: 60000,
        },
      );

      if (!data?.success) {
        toast.error(data?.message || "List load failed");
        return;
      }

      setRows(data.rows || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
      setPage(data.page || p);

      // reset selections on new fetch
      setApproveEmpIds([]);
      setDeleteTransferIds([]);
      setApproveSelectAll(false);
      setDeleteSelectAll(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta().then(() => {
      // after meta, load list
      fetchList(1);
      setPage(1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ---------- autocomplete handlers ----------
  const fetchEmpCodeSug = async (prefix) => {
    try {
      if (!prefix || prefix.length < 1) {
        setCodeSug([]);
        return;
      }
      const { data } = await axios.get(
        `${BASE_URL}/api/payroll/employee-transfer/autocomplete/empcode`,
        {
          params: {
            finYearId: Number(finYearId),
            fromSiteId:
              fromSite === "Select" ? String(branchId) : String(fromSite),
            branchType: String(branchType),
            prefix: prefix,
          },
          headers: axiosHeaders(),
        },
      );
      if (data?.success) setCodeSug(data.items || []);
    } catch {
      // ignore autocomplete errors
    }
  };

  const fetchEmpNameSug = async (prefix) => {
    try {
      if (!prefix || prefix.length < 1) {
        setNameSug([]);
        return;
      }
      const { data } = await axios.get(
        `${BASE_URL}/api/payroll/employee-transfer/autocomplete/empname`,
        {
          params: {
            finYearId: Number(finYearId),
            fromSiteId:
              fromSite === "Select" ? String(branchId) : String(fromSite),
            branchType: String(branchType),
            prefix: prefix,
          },
          headers: axiosHeaders(),
        },
      );
      if (data?.success) setNameSug(data.items || []);
    } catch {
      // ignore
    }
  };

  // selection toggles
  const toggleApproveEmp = (empid) => {
    setApproveEmpIds((prev) =>
      prev.includes(empid) ? prev.filter((x) => x !== empid) : [...prev, empid],
    );
  };

  const toggleDeleteTransfer = (transferId) => {
    setDeleteTransferIds((prev) =>
      prev.includes(transferId)
        ? prev.filter((x) => x !== transferId)
        : [...prev, transferId],
    );
  };

  const handleApproveSelectAll = () => {
    if (approveSelectAll) {
      setApproveEmpIds([]);
      setApproveSelectAll(false);
      return;
    }
    const ids = rows
      .filter((r) => String(r.approve || "").toLowerCase() !== "true") // only not-approved rows selectable
      .map((r) => Number(r.empid))
      .filter(Boolean);
    setApproveEmpIds(ids);
    setApproveSelectAll(true);
  };

  const handleDeleteSelectAll = () => {
    if (deleteSelectAll) {
      setDeleteTransferIds([]);
      setDeleteSelectAll(false);
      return;
    }
    const ids = rows
      .filter((r) => String(r.approve || "").toLowerCase() !== "true") // ASP hides delete for approved
      .map((r) => Number(r.fld_employee_transferemployeeid))
      .filter(Boolean);
    setDeleteTransferIds(ids);
    setDeleteSelectAll(true);
  };

  // actions
  const handleSearch = async (e) => {
    e?.preventDefault?.();
    const dateErr = validateFromTo(fromDate, toDate);
    if (dateErr) {
      toast.warn(dateErr);
      return;
    }
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    await loadMeta();
    setEmpCode("");
    setEmpName("");
    setFromDate("");
    setToDate("");
    setToSite("Select");
    setPage(1);
    await fetchList(1);
  };

  // ✅ Clear — same field reset as Refresh, without re-hitting the meta endpoint
  const handleClearFilters = () => {
    setEmpCode("");
    setEmpName("");
    setFromDate("");
    setToDate("");
    setToSite("Select");
    setCodeSug([]);
    setNameSug([]);
    setPage(1);
    fetchList(1);
  };

  const handleAddNew = () => {
    // ASP clears Session["employeeid"] and goes to entry
    navigate("/payroll/EmployeeTransferEntry"); // change route if needed
  };

  const handleView = (transferId) => {
    // In ASP, it sets Session["employeeid"]=transferId and redirects to EmployeeTransferEntry.aspx
    // In React, pass id in URL
    navigate(`/payroll/EmployeeTransferEntry/${transferId}`);
  };

  const handleApprove = async () => {
    if (approveEmpIds.length === 0) {
      toast.warn("Please select any one");
      return;
    }
    const ok = window.confirm("Are you sure to approve?");
    if (!ok) return;

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${BASE_URL}/api/payroll/employee-transfer/approve`,
        {
          finYearId: Number(finYearId),
          empIds: approveEmpIds,
        },
        { headers: axiosHeaders() },
      );

      if (data?.success) {
        toast.success(data?.message || "Successfully Approved...");
        await fetchList(page);
      } else {
        toast.error(data?.message || "Approve failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (deleteTransferIds.length === 0) {
      toast.warn("Please select any one");
      return;
    }
    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${BASE_URL}/api/payroll/employee-transfer/delete`,
        {
          finYearId: Number(finYearId),
          transferIds: deleteTransferIds,
        },
        { headers: axiosHeaders() },
      );

      if (data?.success) {
        toast.success(data?.message || "Deleted successfully");
        // if last page emptied, move back one page
        const nextPage =
          page > 1 && rows.length === deleteTransferIds.length
            ? page - 1
            : page;
        setPage(nextPage);
        await fetchList(nextPage);
      } else {
        toast.error(data?.message || "Delete failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              EMPLOYEE TRANSFER LISTING
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <ArrowRightLeft
                  className={isDark ? "text-white" : "text-gray-700"}
                />
              </div>

              <div>
                <div
                  className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Employee Transfer Listing
                </div>
                <div
                  className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
                >
                  Search / Pagination / Approve / Delete / View
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark
                    ? "bg-blue-900/40 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark
                    ? "bg-emerald-900/40 text-emerald-100"
                    : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleAddNew} className={primaryBtn}>
                <Plus className="w-4 h-4" />
                Add New
              </button>

              <button
                onClick={() => setShowFilters((v) => !v)}
                className={ghostBtn}
              >
                {showFilters ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="text-lg font-extrabold">Search Criteria</div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className={ghostBtn}
                    disabled={loading}
                  >
                    Clear
                  </button>

                  <button
                    type="button"
                    onClick={handleRefresh}
                    className={ghostBtn}
                    disabled={loading}
                  >
                    <RefreshCcw
                      className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                    />
                    Refresh
                  </button>

                  <button
                    type="submit"
                    className={primaryBtn}
                    disabled={loading}
                  >
                    <Search className="w-4 h-4" />
                    Search
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Emp Code with autocomplete */}
                <div className="relative">
                  <label className={labelBase}>Employee Code</label>
                  <input
                    value={empCode}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEmpCode(v);
                      setCodeOpen(true);
                      fetchEmpCodeSug(v);
                    }}
                    onBlur={() => setTimeout(() => setCodeOpen(false), 150)}
                    placeholder="Type employee code..."
                    className={`${inputBase} mt-2`}
                  />
                  {codeOpen && codeSug.length > 0 && (
                    <div
                      className={`absolute z-30 mt-2 w-full rounded-2xl border shadow-xl overflow-hidden ${
                        isDark
                          ? "bg-gray-900 border-white/10"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      {codeSug.slice(0, 10).map((s, idx) => (
                        <button
                          type="button"
                          key={idx}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            isDark
                              ? "text-gray-200 hover:bg-white/10"
                              : "text-gray-800 hover:bg-gray-50"
                          }`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setEmpCode(s);
                            setCodeOpen(false);
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* From Site */}
                <div>
                  <label className={labelBase}>From Site</label>
                  <select
                    className={`${inputBase} mt-2`}
                    value={fromSite}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFromSite(v);
                      // update autocomplete context
                      setEmpCode("");
                      setEmpName("");
                    }}
                  >
                    <option value="Select">Select</option>
                    {(fromSites || []).map((b, idx) => (
                      <option
                        key={idx}
                        value={String(
                          b.fld_Branchid ?? b.branchid ?? b.id ?? b.value ?? "",
                        )}
                      >
                        {b.fld_BranchName ??
                          b.branchname ??
                          b.name ??
                          b.text ??
                          "Branch"}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Emp Name with autocomplete */}
                <div className="relative">
                  <label className={labelBase}>Employee Name</label>
                  <input
                    value={empName}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEmpName(v);
                      setNameOpen(true);
                      fetchEmpNameSug(v);
                    }}
                    onBlur={() => setTimeout(() => setNameOpen(false), 150)}
                    placeholder="Type employee name..."
                    className={`${inputBase} mt-2`}
                  />
                  {nameOpen && nameSug.length > 0 && (
                    <div
                      className={`absolute z-30 mt-2 w-full rounded-2xl border shadow-xl overflow-hidden ${
                        isDark
                          ? "bg-gray-900 border-white/10"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      {nameSug.slice(0, 10).map((s, idx) => (
                        <button
                          type="button"
                          key={idx}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            isDark
                              ? "text-gray-200 hover:bg-white/10"
                              : "text-gray-800 hover:bg-gray-50"
                          }`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setEmpName(s);
                            setNameOpen(false);
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* To Site */}
                <div>
                  <label className={labelBase}>To Site</label>
                  <select
                    className={`${inputBase} mt-2`}
                    value={toSite}
                    onChange={(e) => setToSite(e.target.value)}
                  >
                    <option value="Select">Select</option>
                    {(toSites || []).map((b, idx) => (
                      <option
                        key={idx}
                        value={String(
                          b.fld_branchid ?? b.branchid ?? b.id ?? b.value ?? "",
                        )}
                      >
                        {b.fld_branchname ??
                          b.branchname ??
                          b.name ??
                          b.text ??
                          "Branch"}
                      </option>
                    ))}
                  </select>
                </div>

                {/* From Date */}
                <div>
                  <label className={labelBase}>From Date</label>
                  <input
                    type="date"
                    value={ddmmyyyyToISO(fromDate)}
                    onChange={(e) => setFromDate(isoToDDMMYYYY(e.target.value))}
                    className={`${inputBase} mt-2`}
                  />
                </div>

                {/* To Date */}
                <div>
                  <label className={labelBase}>To Date</label>
                  <input
                    type="date"
                    value={ddmmyyyyToISO(toDate)}
                    onChange={(e) => setToDate(isoToDDMMYYYY(e.target.value))}
                    className={`${inputBase} mt-2`}
                  />
                </div>
              </div>
            </form>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">
                      Transfer Number
                    </th>
                    <th className="px-4 py-4 text-center font-bold">
                      Transfer Date
                    </th>
                    <th className="px-4 py-4 text-left font-bold">
                      Employee Code
                    </th>
                    <th className="px-4 py-4 text-left font-bold">
                      Employee Name
                    </th>
                    <th className="px-4 py-4 text-center font-bold">
                      Department
                    </th>
                    <th className="px-4 py-4 text-center font-bold">To Site</th>
                    <th className="px-4 py-4 text-center font-bold">
                      Transfer Type
                    </th>
                    <th className="px-4 py-4 text-center font-bold">Status</th>

                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Approve
                        <input
                          type="checkbox"
                          checked={approveSelectAll}
                          onChange={handleApproveSelectAll}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </div>
                    </th>

                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Delete
                        <input
                          type="checkbox"
                          checked={deleteSelectAll}
                          onChange={handleDeleteSelectAll}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr
                        key={idx}
                        className={isDark ? "bg-gray-900" : "bg-white"}
                      >
                        {Array.from({ length: 11 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={11}
                        className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const transferId = Number(
                        r.fld_employee_transferemployeeid,
                      );
                      const approved =
                        String(r.approve || "").toLowerCase() === "true";
                      const empid = Number(r.empid);

                      return (
                        <tr
                          key={`${transferId}-${idx}`}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                                ? "bg-gray-750"
                                : "bg-gray-50"
                          } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                        >
                          <td
                            className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}
                          >
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}
                          >
                            {r.fld_employee_transferemployeecode}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}
                          >
                            {r.trdate}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-200" : "text-gray-900"}`}
                          >
                            <button
                              type="button"
                              onClick={() => handleView(transferId)}
                              className={`font-bold underline underline-offset-2 ${
                                isDark
                                  ? "text-blue-400 hover:text-blue-300"
                                  : "text-blue-600 hover:text-blue-800"
                              }`}
                              title="View / Edit"
                            >
                              {r.code}
                            </button>
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-left font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            {r.empname}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}
                          >
                            {r.depnme}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}
                          >
                            {r.brnme}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}
                          >
                            {r.tpe}
                          </td>

                          <td
                            className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}
                          >
                            {r.status}
                          </td>

                          {/* Approve checkbox */}
                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              disabled={approved || !empid}
                              checked={
                                approved || approveEmpIds.includes(empid)
                              }
                              onChange={() => toggleApproveEmp(empid)}
                              className={`w-4 h-4 ${approved ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                              title={
                                approved
                                  ? "Already approved"
                                  : "Select to approve"
                              }
                            />
                          </td>

                          {/* Delete checkbox */}
                          <td className="px-4 py-4 text-center">
                            {approved ? (
                              <span
                                className={`text-xs font-bold ${isDark ? "text-gray-400" : "text-gray-500"}`}
                              >
                                —
                              </span>
                            ) : (
                              <input
                                type="checkbox"
                                disabled={!transferId}
                                checked={deleteTransferIds.includes(transferId)}
                                onChange={() =>
                                  toggleDeleteTransfer(transferId)
                                }
                                className="w-4 h-4 cursor-pointer"
                                title="Select to delete"
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ==================== APPROVE / REMOVE ==================== */}
            {rows.length > 0 && (
              <div className="flex flex-wrap gap-3 p-4 border-t border-gray-200 dark:border-white/10">
                <button
                  type="button"
                  onClick={handleApprove}
                  className={approveBtn}
                  disabled={approveEmpIds.length === 0 || loading}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Approve Selected
                </button>

                <button
                  type="button"
                  onClick={handleDelete}
                  className={deleteBtn}
                  disabled={deleteTransferIds.length === 0 || loading}
                >
                  🗑️ Remove Selected
                </button>
              </div>
            )}
          </div>

          {/* ==================== PAGINATION ==================== */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div
              className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
            >
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of{" "}
              <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  <ChevronLeft className="w-4 h-4 inline" /> Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    type="button"
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next <ChevronRight className="w-4 h-4 inline" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeTransferListing;
